import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useTheme } from "../components/ThemeProvider";
import { getBookingPalette } from "../components/booking/BookingTheme";
import { BookingProgress } from "../components/booking/BookingProgress";
import {
  BOOKING_FONT_FAMILY,
  EmptyState,
  RetryButton,
  SearchInput,
  StepCard,
} from "../components/booking/BookingPrimitives";
import {
  armenianToLatin,
  fetchOrganizationDetail,
  pickAddress,
  pickTranslation,
  resolveStorageUrl,
} from "../helper/bookingApi";

function parseUrlToken(token) {
  if (!token) return null;
  const [idPart, ...nameParts] = String(token).split(",");
  const id = Number(idPart);
  if (!idPart || Number.isNaN(id)) return null;
  const rawName = nameParts.join(",");
  let urlName = rawName || "";
  try {
    urlName = decodeURIComponent(rawName);
  } catch (_err) {
    urlName = rawName;
  }
  return { id, name: urlName.replace(/-/g, " ").trim(), urlName: urlName.trim() };
}

function makeUrlToken(id, label) {
  if (id == null) return "";
  const safeLabel = armenianToLatin(String(label || id))
    .trim()
    .replace(/[\\/]+/g, "-")
    .replace(/\s+/g, "-");
  return `${id},${encodeURIComponent(safeLabel || id)}`;
}

function getBranches(detail) {
  if (Array.isArray(detail?.branches)) return detail.branches;
  if (Array.isArray(detail?.branch)) return detail.branch;
  if (Array.isArray(detail)) return detail;
  return [];
}

function getOrganization(detail, fallback) {
  if (detail?.organization) return detail.organization;
  if (detail?.organisation) return detail.organisation;
  if (detail && !Array.isArray(detail)) return detail;
  return fallback;
}

function getImagePath(node) {
  return (
    node?.logo_path ||
    node?.logo ||
    node?.image_path ||
    node?.organizationImage ||
    node?.image ||
    ""
  );
}

function getInitials(name) {
  return String(name || "N")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getIndustryName(organization, fallbackOrganization, lang) {
  const candidates = [
    organization?.industry,
    organization?.organization_type,
    organization?.organizationType,
    organization?.type,
    organization?.industry_name,
    organization?.industryName,
    organization?.organization_type_name,
    fallbackOrganization?.industry,
    fallbackOrganization?.organization_type,
    fallbackOrganization?.organizationType,
    fallbackOrganization?.type,
    fallbackOrganization?.industry_name,
    fallbackOrganization?.industryName,
    fallbackOrganization?.organization_type_name,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    const name = pickTranslation(candidate, lang) || candidate?.title || candidate?.label;
    if (name) return name;
  }

  return "";
}

function buildMapQuery({ orgName, branchName, address }) {
  return [branchName || orgName, address].filter(Boolean).join(", ");
}

function buildMapEmbedUrl(query) {
  return query
    ? `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed`
    : "";
}

const WORKING_DAY_LABELS = {
  hy: ["Երկ.", "Երք.", "Չրք.", "Հնգ.", "Ուրբ.", "Շբթ.", "Կիր."],
  en: ["Mon.", "Tue.", "Wed.", "Thu.", "Fri.", "Sat.", "Sun."],
  ru: ["Пн.", "Вт.", "Ср.", "Чт.", "Пт.", "Сб.", "Вс."],
};

const CLOSED_LABELS = {
  hy: "Փակ է",
  en: "Closed",
  ru: "Закрыто",
};

function normalizeLang(lang) {
  return String(lang || "hy").split("-")[0];
}

function formatWorkingTime(value) {
  if (!value) return "";
  return String(value).slice(0, 5);
}

function formatWorkingDayRange(startDay, endDay, lang) {
  const labels = WORKING_DAY_LABELS[normalizeLang(lang)] || WORKING_DAY_LABELS.hy;
  const start = labels[startDay - 1] || startDay;
  const end = labels[endDay - 1] || endDay;
  return startDay === endDay ? start : `${start} - ${end}`;
}

function buildWorkingTimeLines(workingTimes, lang) {
  if (!Array.isArray(workingTimes) || workingTimes.length === 0) return [];

  const normalized = workingTimes
    .map((item) => ({
      day: Number(item?.day),
      working: Number(item?.working) === 1,
      start: formatWorkingTime(item?.day_start),
      end: formatWorkingTime(item?.day_end),
    }))
    .filter((item) => item.day >= 1 && item.day <= 7)
    .sort((a, b) => a.day - b.day);

  const groups = [];
  for (const item of normalized) {
    if (!item.working || !item.start || !item.end) continue;
    const signature = `${item.start}-${item.end}`;
    const last = groups[groups.length - 1];
    if (last && last.signature === signature && item.day === last.endDay + 1) {
      last.endDay = item.day;
      continue;
    }
    groups.push({
      signature,
      startDay: item.day,
      endDay: item.day,
      start: item.start,
      end: item.end,
    });
  }

  return groups.map(
    (group) =>
      `${formatWorkingDayRange(group.startDay, group.endDay, lang)} · ${group.start}-${group.end}`
  );
}

function getBranchWorkingHours(branch, lang) {
  const workingTimeLines = buildWorkingTimeLines(branch?.working_times, lang);
  if (workingTimeLines.length) return workingTimeLines;
  if (Array.isArray(branch?.working_times) && branch.working_times.length > 0) {
    const labelLang = normalizeLang(lang);
    return [CLOSED_LABELS[labelLang] || CLOSED_LABELS.hy];
  }

  const fallback =
    branch?.working_hours_text ||
    branch?.working_hours ||
    branch?.work_hours ||
    branch?.schedule_text ||
    branch?.hours ||
    branch?.open_hours ||
    (branch?.start_time && branch?.end_time
      ? `${branch.start_time} - ${branch.end_time}`
      : "") ||
    (branch?.from_time && branch?.to_time
      ? `${branch.from_time} - ${branch.to_time}`
      : "") ||
    "09:00 - 18:00";
  return [fallback];
}

const INITIAL_BRANCH_VISIBLE_COUNT = 12;
const BRANCH_VISIBLE_INCREMENT = 12;

function BranchCard({ palette, branch, orgName, orgToken, lang, bookingBasePath }) {
  const { t } = useTranslation();
  const branchName = pickTranslation(branch, lang);
  const address = pickAddress(branch) || t("booking.branch.addressFallback");
  const bookingHref = `${bookingBasePath}/${orgToken}/${makeUrlToken(branch.id, branchName)}?step=service`;
  const workingHours = getBranchWorkingHours(branch, lang);

  return (
    <article className="public-org-branch-card">
      <div className="public-org-branch-card__main">
        <span className="public-org-branch-card__pin" aria-hidden="true">
          <img
            src="/assets/images/icons/location.svg"
            alt=""
          />
        </span>
        <div className="public-org-branch-card__copy">
          <h3 className="public-org-branch-card__name">
            {branchName || orgName}
          </h3>
          <p className="public-org-branch-card__address">{address}</p>
        </div>
      </div>

      <div className="public-org-branch-card__bottom">
        <div className="public-org-branch-card__hours">
          <span className="public-org-branch-card__clock" aria-hidden="true" />
          <span>
            <span className="public-org-branch-card__hours-label">
              {t("booking.publicOrg.workingHours")}
            </span>
            {workingHours.map((line) => (
              <span key={line} className="public-org-branch-card__hours-value">
                {line}
              </span>
            ))}
          </span>
        </div>
        <Link
          to={bookingHref}
          className="public-org-branch-card__cta"
        >
          {t("booking.publicOrg.bookBranch")}
        </Link>
      </div>
    </article>
  );
}

const OrganizationPublicPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { bookingOrg } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useMemo(() => parseUrlToken(bookingOrg), [bookingOrg]);
  const navigationOrganization = location.state?.organization;
  const { isDarkMode } = useTheme();
  const isVisuallyDark = !isDarkMode;
  const palette = useMemo(() => getBookingPalette(isVisuallyDark), [isVisuallyDark]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [branchSearch, setBranchSearch] = useState("");
  const [visibleBranchCount, setVisibleBranchCount] = useState(INITIAL_BRANCH_VISIBLE_COUNT);

  const bookingBasePath = lang === "en" ? "/en/online-booking" : "/online-booking";
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(bookingBasePath);
  };

  const load = useCallback(async () => {
    if (!token?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrganizationDetail(lang, token.id);
      setDetail(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [lang, token?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const fallbackOrg = useMemo(
    () =>
      token
        ? {
            ...(navigationOrganization || {}),
            id: token.id,
            name: navigationOrganization?.name || token.name,
            urlName: navigationOrganization?.urlName || token.urlName,
          }
        : null,
    [navigationOrganization, token],
  );
  const organization = getOrganization(detail, fallbackOrg) || fallbackOrg || {};
  const branches = useMemo(() => getBranches(detail), [detail]);
  const normalizedBranchSearch = branchSearch.trim().toLowerCase();
  const filteredBranches = useMemo(() => {
    if (!normalizedBranchSearch) return branches;
    return branches.filter((branch) => {
      const branchName = pickTranslation(branch, lang).toLowerCase();
      const branchAddress = pickAddress(branch).toLowerCase();
      return (
        branchName.includes(normalizedBranchSearch) ||
        branchAddress.includes(normalizedBranchSearch)
      );
    });
  }, [branches, lang, normalizedBranchSearch]);
  const visibleBranches = filteredBranches.slice(0, visibleBranchCount);
  const hasBranchTools = branches.length > 3;
  const remainingBranchCount = Math.max(filteredBranches.length - visibleBranches.length, 0);
  const orgName = pickTranslation(organization, lang);
  const displayOrgName = orgName || (!loading ? t("booking.publicOrg.fallbackName") : "");
  const industryName = getIndustryName(organization, navigationOrganization, lang);
  const orgAddress = pickAddress(organization);
  const logo = resolveStorageUrl(getImagePath(organization));
  const orgToken = makeUrlToken(token?.id || organization?.id, displayOrgName || token?.name);
  const firstBranch = filteredBranches[0] || branches[0];
  const mapQuery = buildMapQuery({
    orgName: displayOrgName,
    branchName: firstBranch ? pickTranslation(firstBranch, lang) : "",
    address: firstBranch ? pickAddress(firstBranch) : orgAddress,
  });
  const mapUrl = buildMapEmbedUrl(mapQuery);

  useEffect(() => {
    setVisibleBranchCount(INITIAL_BRANCH_VISIBLE_COUNT);
  }, [branchSearch, branches.length]);

  return (
    <>
      <section
        className="public-org-page"
        style={{
          fontFamily: BOOKING_FONT_FAMILY,
          background: palette.pageBg,
          color: palette.text,
          paddingBottom: "clamp(60px, 10vw, 120px)",
        }}
      >
        <div className="public-org-gradient-strip" />
        <div className="container container-two public-org-progress-wrap">
          <div className="public-org-progress-shell">
            <BookingProgress palette={palette} current={1} completed={[0]} />
          </div>
        </div>

        <div className="container container-two public-org-content-wrap">
          <div className="public-org-layout">
            <StepCard palette={palette}>
              <div className="public-org-main-card">
                <div className="public-org-breadcrumb">
                  <button
                    type="button"
                    className="public-org-back-button"
                    onClick={handleBack}
                  >
                    <span className="public-org-back-button__icon" aria-hidden="true" />
                    {t("booking.common.back")}
                  </button>
                  <span>
                    {industryName ? `${industryName} / ` : ""}
                    {displayOrgName}
                  </span>
                </div>
                {loading ? (
                  <div
                    style={{
                      minHeight: 280,
                      borderRadius: 18,
                      background: palette.surfaceAlt,
                      border: palette.border,
                    }}
                  />
                ) : error ? (
                  <EmptyState
                    palette={palette}
                    icon="las la-exclamation-circle"
                    title={t("booking.publicOrg.loadError")}
                    action={<RetryButton palette={palette} onClick={load} />}
                  />
                ) : (
                  <>
                    <div className="public-org-identity">
                      <span className="public-org-logo">
                        {logo ? <img src={logo} alt="" /> : getInitials(displayOrgName)}
                      </span>
                      <div className="public-org-identity__copy">
                        <h1>{displayOrgName}</h1>
                        {orgAddress ? <p>{orgAddress}</p> : null}
                      </div>
                      <span className="public-org-total-branches">
                        <img src="/assets/images/icons/location.svg" alt="" />
                        {t("booking.organization.branchCount", { count: branches.length })}
                      </span>
                    </div>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                      <div>
                        <h2
                          className="fw-800 mb-1"
                          style={{ color: palette.text, fontSize: 16, lineHeight: "22px" }}
                        >
                          {t("booking.publicOrg.organizationBranches")}
                        </h2>
                        <p
                          className="mb-0"
                          style={{ color: palette.textMuted, fontSize: 12, lineHeight: "18px" }}
                        >
                          {t("booking.publicOrg.branchesSubtitle")}
                        </p>
                      </div>
                    </div>

                    {branches.length ? (
                      <>
                        {hasBranchTools ? (
                          <div className="public-org-branch-tools">
                            <SearchInput
                              palette={palette}
                              value={branchSearch}
                              onChange={setBranchSearch}
                              placeholder={t("booking.publicOrg.searchBranch")}
                            />
                          </div>
                        ) : null}

                        {filteredBranches.length ? (
                          <>
                            <div
                              className={`public-org-branches${branches.length <= 3 ? " public-org-branches--small" : ""
                                }`}
                            >
                              {visibleBranches.map((branch) => (
                                <BranchCard
                                  key={branch.id}
                                  palette={palette}
                                  branch={branch}
                                  orgName={displayOrgName}
                                  orgToken={orgToken}
                                  lang={lang}
                                  bookingBasePath={bookingBasePath}
                                />
                              ))}
                            </div>
                            {remainingBranchCount ? (
                              <div className="public-org-show-more">
                                <button
                                  type="button"
                                  className="pill fw-700"
                                  onClick={() =>
                                    setVisibleBranchCount(
                                      (count) => count + BRANCH_VISIBLE_INCREMENT,
                                    )
                                  }
                                  style={{
                                    background: palette.accent,
                                    color: "#061415",
                                    border: "none",
                                    padding: "10px 18px",
                                  }}
                                >
                                  {t("booking.publicOrg.showMore", {
                                    count: Math.min(
                                      BRANCH_VISIBLE_INCREMENT,
                                      remainingBranchCount,
                                    ),
                                  })}
                                </button>
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <EmptyState
                            palette={palette}
                            icon="las la-search"
                            title={t("booking.publicOrg.noMatchingBranches")}
                          />
                        )}
                      </>
                    ) : (
                      <EmptyState
                        palette={palette}
                        icon="las la-map-marker"
                        title={t("booking.publicOrg.noBranches")}
                      />
                    )}
                  </>
                )}
              </div>
            </StepCard>

            <aside className="public-org-map-card">
              <StepCard palette={palette}>
                <div style={{ padding: "clamp(14px, 3vw, 18px)", display: "grid", gap: 14 }}>
                  <div>
                    <h2
                      className="fw-800 mb-1"
                      style={{ color: palette.text, fontSize: 16, lineHeight: "22px" }}
                    >
                      {t("booking.publicOrg.mapTitle")}
                    </h2>
                    <p
                      className="mb-0"
                      style={{ color: palette.textMuted, fontSize: 12, lineHeight: "18px" }}
                    >
                      {mapQuery || t("booking.publicOrg.mapFallback")}
                    </p>
                  </div>
                  <div
                    style={{
                      minHeight: 220,
                      borderRadius: 18,
                      overflow: "hidden",
                      border: palette.border,
                      background: palette.surfaceAlt,
                    }}
                  >
                    {mapUrl ? (
                      <iframe
                        title={t("booking.publicOrg.mapTitle")}
                        src={mapUrl}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                      />
                    ) : (
                      <div
                        className="h-100 d-grid text-center"
                        style={{
                          placeItems: "center",
                          color: palette.textMuted,
                          padding: 24,
                        }}
                      >
                        <div>
                          <i
                            className="las la-map-marked-alt d-block mb-2"
                            style={{ fontSize: 44, color: palette.accent }}
                            aria-hidden="true"
                          />
                          {t("booking.publicOrg.mapFallback")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </StepCard>
            </aside>
          </div>
        </div>
      </section>

      <style>{`
        .public-org-page,
        .public-org-page * {
          font-family: ${BOOKING_FONT_FAMILY} !important;
          letter-spacing: 0 !important;
          font-synthesis-weight: none;
        }

        .public-org-page h1,
        .public-org-page h2,
        .public-org-page h3,
        .public-org-page h4,
        .public-org-page .fw-800,
        .public-org-page .fw-700,
        .public-org-page .fw-600,
        .public-org-page .fw-500,
        .public-org-page button,
        .public-org-page a,
        .public-org-page .pill {
          font-family: ${BOOKING_FONT_FAMILY} !important;
          font-weight: 700 !important;
          letter-spacing: 0 !important;
        }

        .public-org-page p,
        .public-org-page input,
        .public-org-page textarea {
          font-family: ${BOOKING_FONT_FAMILY} !important;
          font-weight: 400 !important;
          letter-spacing: 0 !important;
        }

        .public-org-gradient-strip {
          height: 58px;
          background: var(--main-gradient);
        }

        .public-org-progress-wrap {
          margin-top: -30px;
          position: relative;
          z-index: 2;
        }

        .public-org-progress-shell {
          background: ${palette.surface};
          border: ${palette.border};
          border-radius: 14px;
          padding: 10px 24px 5px;
          box-shadow: ${palette.shadow};
        }

        .public-org-content-wrap {
          margin-top: 12px;
        }

        .public-org-main-card {
          padding: 16px;
        }

        .public-org-breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          margin-bottom: 24px;
          color: ${palette.textMuted};
          font-size: 12px;
          line-height: 18px;
        }

        .public-org-breadcrumb > span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .public-org-identity {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
          margin-bottom: 18px;
        }

        .public-org-logo {
          width: 42px;
          height: 42px;
          flex: 0 0 42px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border: ${palette.border};
          border-radius: 50%;
          background: #fff;
          color: ${palette.accent};
          font-size: 14px;
          font-weight: 700;
        }

        .public-org-logo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .public-org-identity__copy {
          flex: 1 1 auto;
          min-width: 0;
        }

        .public-org-identity__copy h1 {
          margin: 0;
          color: ${palette.text};
          font-size: 18px;
          line-height: 24px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .public-org-identity__copy p {
          margin: 2px 0 0;
          color: ${palette.textMuted};
          font-size: 12px;
          line-height: 18px;
        }

        .public-org-total-branches {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          flex: 0 0 auto;
          color: ${palette.accent};
          font-size: 12px;
          line-height: 18px;
          white-space: nowrap;
        }

        .public-org-total-branches img {
          width: 14px;
          height: 14px;
        }

        .public-org-hero {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }

        .public-org-hero-stack {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
        }

        .public-org-back-button:hover,
        .public-org-back-button:focus-visible {
          border-color: rgba(255, 255, 255, 0.86) !important;
          color: #ffffff !important;
          outline: none;
        }

        .public-org-back-button {
          min-height: 30px;
          padding: 5px 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          flex: 0 0 auto;
          border: ${palette.border};
          border-radius: 40px;
          background: transparent;
          color: ${palette.textMuted};
          font-size: 12px;
          line-height: 18px;
        }

        .public-org-back-button__icon {
          width: 16px;
          height: 16px;
          position: relative;
          display: inline-block;
          flex: 0 0 16px;
        }

        .public-org-back-button__icon::before {
          content: "";
          position: absolute;
          left: 2px;
          top: 50%;
          width: 12px;
          height: 2px;
          background: currentColor;
          transform: translateY(-50%);
          border-radius: 999px;
        }

        .public-org-back-button__icon::after {
          content: "";
          position: absolute;
          left: 2px;
          top: 50%;
          width: 8px;
          height: 8px;
          border-left: 2px solid currentColor;
          border-bottom: 2px solid currentColor;
          transform: translateY(-50%) rotate(45deg);
        }

        .public-org-layout {
          display: grid;
          grid-template-columns: minmax(0, 2.15fr) minmax(280px, 0.9fr);
          gap: 12px;
          align-items: start;
        }

        .public-org-map-card {
          position: sticky;
          top: 96px;
        }

        .public-org-branch-tools {
          margin-bottom: 12px;
        }

        .public-org-branch-count {
          padding: 10px 13px;
          white-space: nowrap;
          font-size: 0.84rem;
        }

        .public-org-branch-card {
          min-height: 116px;
          background: ${palette.surfaceAlt};
          border: ${palette.border};
          border-radius: 12px;
          padding: 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .public-org-branch-card__main {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          min-width: 0;
        }

        .public-org-branch-card__pin {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: ${palette.accentSoft};
          border: 1px solid ${palette.accentBorder};
          color: ${palette.accent};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 36px;
          position: relative;
        }

        .public-org-branch-card__pin i {
          font-size: 24px;
          line-height: 1;
        }

        .public-org-branch-card__copy {
          min-width: 0;
          flex: 1 1 auto;
        }

        .public-org-branch-card__name {
          color: ${palette.text};
          font-size: 14px;
          font-weight: 700;
          line-height: 20px;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .public-org-branch-card__address {
          color: ${palette.textMuted};
          font-size: 12px;
          font-weight: 400;
          line-height: 18px;
          margin: 3px 0 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .public-org-branch-card__bottom {
          min-height: 34px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .public-org-branch-card__hours {
          display: inline-flex;
          align-items: flex-start;
          gap: 6px;
          color: ${palette.textMuted};
          min-width: 0;
        }

        .public-org-branch-card__clock {
          width: 16px;
          height: 16px;
          border: 1.5px solid currentColor;
          border-radius: 999px;
          flex: 0 0 16px;
          margin-top: 2px;
          position: relative;
        }

        .public-org-branch-card__clock::before,
        .public-org-branch-card__clock::after {
          content: "";
          position: absolute;
          left: 50%;
          top: 50%;
          background: currentColor;
          border-radius: 999px;
          transform-origin: left center;
        }

        .public-org-branch-card__clock::before {
          width: 4px;
          height: 1.5px;
          transform: translateY(-50%);
        }

        .public-org-branch-card__clock::after {
          width: 1.5px;
          height: 5px;
          transform: translate(-50%, -100%);
        }

        .public-org-branch-card__hours-label,
        .public-org-branch-card__hours-value {
          display: block;
          white-space: nowrap;
        }

        .public-org-branch-card__hours-label {
          font-size: 11px;
          font-weight: 400;
          line-height: 19.2px;
        }

        .public-org-branch-card__hours-value {
          font-size: 11px;
          font-weight: 400;
          line-height: 16px;
        }

        .public-org-branch-card__cta {
          border: 1px solid ${isVisuallyDark ? "#ffffff" : "#111315"};
          border-radius: 40px;
          color: ${isVisuallyDark ? "#ffffff" : "#111315"};
          padding: 9px 16px;
          background: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          text-decoration: none;
          font-size: 11px;
          font-weight: 700;
          line-height: 14px;
          white-space: nowrap;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .public-org-branch-card__cta:hover,
        .public-org-branch-card__cta:focus-visible {
          background: ${isVisuallyDark ? "rgba(255, 255, 255, 0.08)" : "#111315"};
          color: #ffffff;
          outline: none;
        }

        .public-org-branches {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }

        .public-org-branches--small {
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        }

        .public-org-show-more {
          display: flex;
          justify-content: center;
          margin-top: 14px;
        }

        @media (max-width: 991px) {
          .public-org-layout {
            grid-template-columns: 1fr;
          }

          .public-org-map-card {
            position: static;
          }
        }

        @media (max-width: 767px) {
          .public-org-hero {
            flex-direction: column;
            align-items: flex-start;
          }

          .public-org-back-button {
            min-height: 42px !important;
            padding: 9px 19px !important;
            font-size: 16px !important;
            white-space: nowrap !important;
            flex-shrink: 0;
          }

          .public-org-branch-tools {
            grid-template-columns: 1fr;
          }

          .public-org-branch-count {
            justify-self: flex-start;
          }

          .public-org-branches {
            grid-template-columns: 1fr;
          }

          .public-org-branch-card__name,
          .public-org-branch-card__address {
            white-space: normal;
          }

          .public-org-branch-card__bottom {
            align-items: flex-start;
          }
        }
      `}</style>
    </>
  );
};

export default OrganizationPublicPage;
